import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaTimes, FaChevronDown, FaChevronUp, FaCheck } from 'react-icons/fa';
import ResearchTokenIntelligence from './ResearchTokenIntelligence';
import BucketDataView from './BucketDataView';
import { TradingViewResearch } from '../TradingViewResearch';

export default function ResearchResultDetail({
  portfolioId,
  portfolioName,
  portfolioType,
  isTestnet,
  apiBaseUrl: apiBaseUrlProp,
  authToken,
  result,
  onBack,
  onApplied,
}) {
  const apiBaseUrl = apiBaseUrlProp || window.location.origin.replace('/web', '');

  const [selectedTokens, setSelectedTokens] = useState(new Set(result.proposed_tokens));
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [showMetadata, setShowMetadata] = useState(false);

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return (
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' +
      date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
  };

  const isExpired = new Date(result.expires_at) < new Date();

  const handleTokenToggle = (token) => {
    setSelectedTokens((prev) => {
      const next = new Set(prev);
      if (next.has(token)) {
        next.delete(token);
      } else {
        next.add(token);
      }
      return next;
    });
  };

  const handleSelectAll = () => setSelectedTokens(new Set(result.proposed_tokens));
  const handleDeselectAll = () => setSelectedTokens(new Set());

  const getScoreColor = (score) => {
    if (score >= 0.7) return 'bg-green-500';
    if (score >= 0.5) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  const handleApply = async () => {
    setApplyError(null);
    setIsApplying(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const response = await fetch(
        `${apiBaseUrl}/portfolio/${portfolioId}/research/results/${result.id}/apply`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ tokens: Array.from(selectedTokens) }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      onApplied(data);
    } catch (err) {
      console.error('Failed to apply research result:', err);
      setApplyError(err.message || 'Failed to apply research result');
    } finally {
      setIsApplying(false);
    }
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[100] bg-black/50" onClick={onBack} />

      {/* Slide-over panel — full-screen on mobile, slide-over on sm+ */}
      <div className="fixed inset-0 sm:inset-y-0 sm:left-auto sm:right-0 sm:w-full sm:max-w-2xl z-[101] bg-slate-900 sm:border-l border-slate-700 overflow-y-auto shadow-2xl">
        {/* Header bar */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-900/40 border border-cyan-700/50 text-cyan-300">
              {result.bucket}
            </span>
            {isExpired && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/40 border border-red-700/50 text-red-300">
                EXPIRED
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
          {/* Run info */}
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <span>Run: {formatDateTime(result.created_at)}</span>
            <span>Expires: {formatDateTime(result.expires_at)}</span>
          </div>

          {/* Summary section */}
          <section>
            {/* <h3 className="text-lg font-semibold text-white mb-4">Summary</h3> */}
            <div className="prose prose-invert prose-sm max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline" />
                  ),
                }}
              >
                {result.summary_markdown}
              </ReactMarkdown>
            </div>
          </section>

          {/* Token Intelligence: sentiment + news for proposed tokens */}
          <section className="border-t border-slate-700/50 pt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Token Intelligence</h3>
            <ResearchTokenIntelligence
              tokens={result.proposed_tokens}
              apiBaseUrl={apiBaseUrl}
            />
          </section>

          {/* Proposed Tokens section */}
          <section className="border-t border-slate-700/50 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Proposed Token Set ({result.proposed_tokens.length} tokens)
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
                >
                  Select All
                </button>
                <span className="text-slate-600">|</span>
                <button
                  onClick={handleDeselectAll}
                  className="text-slate-400 hover:text-slate-300 text-sm font-medium"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {result.token_proposals.map((proposal, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${selectedTokens.has(proposal.symbol) ? 'bg-slate-800/80 border-slate-600' : 'bg-slate-800/30 border-slate-700/50 opacity-75'}`}
                >
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      checked={selectedTokens.has(proposal.symbol)}
                      onChange={() => handleTokenToggle(proposal.symbol)}
                      disabled={isExpired}
                      className="w-5 h-5 mt-0.5 text-cyan-600 bg-slate-700 border-slate-600 rounded focus:ring-cyan-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xl font-bold text-white">{proposal.symbol}</span>
                        <TradingViewResearch
                          pair={`${proposal.symbol}/USD`}
                          portfolioType={portfolioType}
                          isTestnet={isTestnet}
                        />
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getScoreColor(proposal.score)} bg-opacity-20 text-white`}>
                          Score: {proposal.score.toFixed(2)}
                        </span>
                      </div>
                      <div className="mb-3">
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getScoreColor(proposal.score)}`}
                            style={{ width: `${proposal.score * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-slate-300 text-sm">
                        <ReactMarkdown allowedElements={['strong', 'code', 'em']}>
                          {proposal.rationale}
                        </ReactMarkdown>
                      </div>
                      {Object.keys(proposal.bucket_data || {}).length > 0 && (
                        <BucketDataView bucket={result.bucket} data={proposal.bucket_data} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Metadata section */}
          {result.result_metadata && (
            <section className="border-t border-slate-700/50 pt-6">
              <button
                onClick={() => setShowMetadata(!showMetadata)}
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
              >
                {showMetadata ? <FaChevronUp className="w-4 h-4" /> : <FaChevronDown className="w-4 h-4" />}
                <span className="font-medium">Data Sources & Metadata</span>
              </button>

              {showMetadata && (
                <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Tools Called</h4>
                  <ul className="list-disc list-inside text-sm text-slate-400 space-y-1">
                    {result.result_metadata.tools_called?.map((tool, idx) => (
                      <li key={idx}>{tool}</li>
                    ))}
                  </ul>

                  {result.result_metadata.data_timestamps && (
                    <>
                      <h4 className="text-sm font-medium text-slate-300 mt-4 mb-2">Data Timestamps</h4>
                      <ul className="list-disc list-inside text-xs text-slate-400 space-y-1">
                        {Object.entries(result.result_metadata.data_timestamps).map(([key, value]) => (
                          <li key={key}>{key}: {new Date(value).toLocaleString()}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {result.result_metadata.tools_unavailable?.length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-slate-300 mt-4 mb-2">Unavailable Tools</h4>
                      <ul className="list-disc list-inside text-xs text-amber-400 space-y-1">
                        {result.result_metadata.tools_unavailable.map((tool, idx) => (
                          <li key={idx}>{tool}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Apply button */}
          <section className="border-t border-slate-700/50 pt-6 pb-8">
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-2">Apply to Portfolio</h3>
              <p className="text-slate-400 text-sm mb-4">
                Apply the selected tokens to replace your current portfolio token set.
              </p>

              {applyError && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-red-300 text-sm">{applyError}</div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowApplyConfirm(true)}
                disabled={selectedTokens.size === 0 || isExpired || isApplying}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  selectedTokens.size === 0 || isExpired || isApplying
                    ? 'bg-slate-600/50 text-slate-400 cursor-not-allowed'
                    : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                }`}
              >
                {isApplying ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Applying...
                  </>
                ) : (
                  <>
                    <FaCheck className="w-5 h-5" />
                    Apply {selectedTokens.size} Token{selectedTokens.size !== 1 ? 's' : ''} to Portfolio
                  </>
                )}
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* Apply Confirmation Dialog */}
      {showApplyConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowApplyConfirm(false)} />
          <div className="relative bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Apply Token Set?</h3>
            <p className="text-slate-300 text-sm mb-4">
              Apply these {selectedTokens.size} tokens to{' '}
              <span className="font-medium text-white">{portfolioName}</span>? This will replace the current token set.
            </p>
            <div className="mb-4 p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-2 flex-wrap">
                {Array.from(selectedTokens).map((token) => (
                  <span key={token} className="px-2 py-0.5 bg-cyan-900/40 border border-cyan-700/50 text-cyan-300 text-xs font-medium rounded">
                    {token}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowApplyConfirm(false)}
                disabled={isApplying}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors border border-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={isApplying}
                className={`flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors ${isApplying ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isApplying ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Applying...
                  </>
                ) : (
                  'Apply'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
